// The nflverse adapter: the first real implementation of ICD-S07.
//
// Four operations, and the split between them is the whole contract:
//
//   plan     pure — watermarks and a clock in, a list of resources out
//   probe    43 bytes, to find out whether fetching is worth it at all
//   fetch    bytes, verbatim, nothing parsed
//   extract  pure — bytes in, candidate entities and facts out
//
// It mints no ids and writes nothing. Everything it emits is keyed the way
// nflverse keys things; the registry turns those into ids that are ours. That
// separation is why replacing a provider costs a transform rather than a data
// migration across every roster.

import type {
  ExtractContext,
  ExtractResult,
  FetchContext,
  FetchItem,
  FetchPlan,
  PlanContext,
  ProbeResult,
  SourceAdapter,
  StoredCapture,
} from '../../acquisition/adapter';
import { nflverse } from './manifest';
import { extractSchedules } from './schedules';
import { extractPlayers } from './players';
import { extractRosters } from './rosters';
import { extractInjuries } from './injuries';
import { extractStatLines } from './stats';
import { extractTeamStatLines } from './team-stats';
import { extractDepthCharts } from './depth';
import { NFLVERSE_CONCEPTS, NFLVERSE_MAPPINGS } from './vocabulary';

const RELEASES = 'https://github.com/nflverse/nflverse-data/releases/download';

/** The resources this adapter serves. */
export const SCHEDULES = 'schedules/games';
export const PLAYERS = 'players/players';
export const ROSTERS = 'rosters/roster';
export const INJURIES = 'injuries/injuries';
/** The WEEKLY asset. `stats_player` alone names four files and three are totals. */
export const STATS = 'stats_player/stats_player_week';
/** The club-grained weekly asset. Same release family, same four-variant trap. */
export const TEAM_STATS = 'stats_team/stats_team_week';
export const DEPTH = 'depth_charts/depth_charts';

/**
 * How much of the depth chart file to fetch. The newest snapshot is the first
 * ~213 KB of 46.8 MB and the rest is history; see `depth.ts` for the guards
 * that make reading a prefix safe.
 */
export const DEPTH_WINDOW_BYTES = 2_000_000;

/**
 * How many seasons of history a windowed resource fetches, this one included.
 *
 * Five, settled 2026-09-13. A nine-year-old stat line says nothing about this
 * Sunday — the rules, the league and the roster have all moved — and the
 * binding limit was never storage. See PLAN.md, "How much history".
 */
export const SEASONS_OF_STATS = 5;

/**
 * Which resources run, in the order they run.
 *
 * `partitioned` means one asset per season, named `<asset>_<season>.csv`.
 * Players first: it is the widest directory, so rosters and injuries mostly
 * match against people who already exist rather than minting them. Correctness
 * does not depend on the order — each resource asserts every person it names —
 * but the review queue is quieter this way.
 */
const PLANNED: readonly {
  resource: string;
  partitioned: boolean;
  seasons?: number;
  maxBytes?: number;
}[] = [
  { resource: SCHEDULES, partitioned: false },
  { resource: PLAYERS, partitioned: false },
  { resource: ROSTERS, partitioned: true },
  { resource: INJURIES, partitioned: true },
  // Five seasons, not one. Fantasy needs little history, and the game changes
  // fast enough that old seasons stop being a fair comparison. A roster or an
  // injury report is only ever about this week; a stat line is the one
  // resource whose POINT is the weeks behind it. See PLAN.md, Phase 7.5.
  { resource: STATS, partitioned: true, seasons: SEASONS_OF_STATS },
  // The same window for the club lines, because a defense's history is scored
  // over the same seasons a player's is. About 230 KB a season.
  { resource: TEAM_STATS, partitioned: true, seasons: SEASONS_OF_STATS },
  // Current season only, and only the top of it. A depth chart is a
  // current-state question: nobody asks who was third-string in October 2023.
  { resource: DEPTH, partitioned: true, maxBytes: DEPTH_WINDOW_BYTES },
];

/** Every resource this adapter fetches, for the credits (`resourcesRead`). */
export const NFLVERSE_READS: readonly string[] = PLANNED.map((p) => p.resource);

/**
 * nflverse publishes a 43-byte `timestamp.json` per release.
 *
 * Measured 2026-09-12: `{"last_updated":"2026-09-12 22:46:25 EDT"}`. Checking it
 * costs three orders of magnitude less than re-fetching the 2.2 MB schedules
 * asset — and five orders less than the 85 MB play-by-play one, which is the
 * case this matters for.
 *
 * It is per RELEASE, so every partition of a season-split resource shares one.
 * That is a real limitation and it is the safe direction: the token moves when
 * any season moves, so a partition is re-fetched when it did not need to be,
 * and never skipped when it did. The content hash settles it either way.
 */
function timestampUrl(resource: string): string {
  const release = resource.split('/')[0]!;
  return `${RELEASES}/${release}/timestamp.json`;
}

function assetUrl(resource: string, partition?: string): string {
  const [release, asset] = resource.split('/');
  const name = partition === undefined ? asset : `${asset}_${partition}`;
  return `${RELEASES}/${release}/${name}.csv`;
}

/**
 * Which season a date falls in.
 *
 * The NFL season is labeled by the calendar year it starts in and runs into
 * February, so January belongs to the year before. March is the boundary: free
 * agency opens the new league year, and nflverse's assets follow it. Pure —
 * the clock arrives as an argument, so a plan is reproducible.
 */
export function seasonOf(now: string): number {
  const d = new Date(now);
  const year = d.getUTCFullYear();
  return d.getUTCMonth() + 1 >= 3 ? year : year - 1;
}

/**
 * NFLVERSE'S REBUILDS, in season and in UTC (nflverse-pbp
 * `.github/workflows/update_data.yaml`, read 2026-09-26; it runs in months 1,
 * 2 and 9 to 12): Sunday 22:03 after the early games, Monday 00:07 after the
 * late ones, and 05:33 on Friday, Monday and Tuesday after the night games —
 * plus 09:03 daily, which our 09:00 cadence sits just before. A rebuild lands
 * one to three hours later.
 *
 * WHAT ACTUALLY RUNS IS EARLIER (read 2026-10-21, `temp/research/` that day):
 * since 2026-09-17 a maintainer's dispatcher starts the build at 09:00 daily,
 * 21:00 Sunday, 00:00 Monday and 04:30 Monday, Tuesday and Friday, and it
 * publishes about five minutes later — every game of weeks 2 to 6 was in the
 * file about an hour after its final whistle. The cron times above are the
 * fallback, which GitHub fired two to ten hours late in 2026. They are kept
 * here because they are the schedule nflverse writes down: our checks still
 * catch every dispatched build within two hours (04:30's at 06:30, the rest
 * within an hour and a half), and a game is due
 * by the fallback's clock, so the alarm sounds only when both have failed.
 *
 * The game-day check and a game's due time are both read from this one list,
 * so they cannot disagree about the schedule.
 */
const REBUILDS: readonly { days: readonly number[]; hour: number; minute: number }[] = [
  { days: [0], hour: 22, minute: 3 },
  { days: [1], hour: 0, minute: 7 },
  { days: [1, 2, 5], hour: 5, minute: 33 },
  { days: [0, 1, 2, 3, 4, 5, 6], hour: 9, minute: 3 },
];

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;
/** "One to three hours later." */
const REBUILD_LANDS = 3 * HOUR;
/** The daily run's hour: the manifest's cadence, `0 9 * * *`. */
export const DAILY_READ_HOUR = 9;
/**
 * How long after its kickoff a game can be in a rebuild: about three and a
 * quarter hours of game, and room for overtime.
 */
const GAME_LENGTH = 4 * HOUR;

function inSeason(ms: number): boolean {
  const month = new Date(ms).getUTCMonth() + 1;
  return month <= 2 || month >= 9;
}

/** nflverse's rebuilds in `[from, to)`, in order. */
function rebuildsBetween(from: number, to: number): number[] {
  const out: number[] = [];
  for (let day = Math.floor(from / DAY) * DAY; day < to; day += DAY) {
    const weekday = new Date(day).getUTCDay();
    for (const r of REBUILDS) {
      if (!r.days.includes(weekday)) continue;
      const at = day + r.hour * HOUR + r.minute * MINUTE;
      if (at >= from && at < to && inSeason(at)) out.push(at);
    }
  }
  return out.sort((a, b) => a - b);
}

/**
 * THE GAME-DAY CHECK: once an hour, and it plans nothing except after one of
 * nflverse's rebuilds.
 *
 * A rebuild lands one to three hours after it starts (`REBUILDS`), so this
 * asks at half past each hour until the first half past that is three hours
 * on. The probe reads a 43-byte timestamp first, so an hour with nothing new
 * fetches nothing more.
 *
 * THE DAILY REBUILD TOO (2026-10-21): our daily run sits three minutes before
 * it, so without these checks a game that missed the night's rebuild — the
 * 10 p.m. Eastern half of a Monday doubleheader, which ends after 05:33 —
 * would wait a day for the next morning's read, and its week settles at
 * 14:00 that Tuesday (DIRECTION D11).
 *
 * Only what a score needs: this season's player and team lines, and the
 * schedule with its results. Everything else waits for the daily run.
 */
export const GAME_DAY_CRON = '30 * * * *';

/** In season, and in the hours after an nflverse rebuild that it can still land in. */
export function afterRebuild(now: string): boolean {
  const t = Date.parse(now);
  if (!inSeason(t)) return false;
  // The hour's check, at half past, and a rebuild in the three and a half hours before it.
  const check = Math.floor(t / HOUR) * HOUR + 30 * MINUTE;
  return rebuildsBetween(check - REBUILD_LANDS - 30 * MINUTE, check).length > 0;
}

/** Whether this worker reads nflverse's lines at `t`: the daily run, or a game-day check. */
function readsAt(t: number): boolean {
  const d = new Date(t);
  if (d.getUTCMinutes() === 0) return d.getUTCHours() === DAILY_READ_HOUR;
  return d.getUTCMinutes() === 30 && afterRebuild(d.toISOString());
}

/**
 * WHEN A GAME'S STAT LINES ARE DUE HERE (ICD-S07 §4, DIRECTION D11): our first
 * read after the first rebuild that can hold the game has had its three hours
 * to land. A Sunday early game is due at half past one on Monday morning, UTC;
 * a Monday night game at nine on Tuesday morning; a Saturday game, which waits
 * for Sunday's daily rebuild, at half past twelve on Sunday. Null out of
 * season, when nflverse does not rebuild.
 *
 * Pure, and on the generous side by design: the alarm it sets
 * (`workers/ingest/src/late-lines.ts`) is for something broken, and a game
 * whose lines came with an earlier rebuild than this assumes is simply early.
 */
export function linesDueBy(kickoff: string): string | null {
  const k = Date.parse(kickoff);
  if (Number.isNaN(k)) return null;
  const [rebuild] = rebuildsBetween(k + GAME_LENGTH, k + GAME_LENGTH + 2 * DAY);
  if (rebuild === undefined) return null;
  const landed = rebuild + REBUILD_LANDS;
  const step = 30 * MINUTE;
  for (let t = Math.ceil(landed / step) * step; t < landed + 2 * DAY; t += step) {
    if (readsAt(t)) return new Date(t).toISOString();
  }
  return null;
}

function gameDayPlan(now: string): FetchPlan {
  if (!afterRebuild(now)) {
    return { items: [], because: `not in the hours after an nflverse rebuild (${now})` };
  }
  const season = String(seasonOf(now));
  return {
    items: [
      { resource: SCHEDULES, url: assetUrl(SCHEDULES) },
      { resource: STATS, url: assetUrl(STATS, season), partition: season },
      { resource: TEAM_STATS, url: assetUrl(TEAM_STATS, season), partition: season },
    ],
    because: `game-day check after a rebuild: season ${season} lines and the schedule, as of ${now}`,
  };
}

/** A User-Agent that says who is calling. Politeness, and it costs nothing. */
const UA = 'fsp/0.1 (+https://fsp-555.pages.dev)';

/**
 * The first `max` bytes of a response, however long the response is.
 *
 * Truncation is expected and is already the shape a 206 produces: the last CSV
 * row arrives half-written and the parser drops it, which is why a capped
 * capture is only ever used for a resource whose rows are independent.
 *
 * Falls back to buffering when there is no readable stream — a mock in a test,
 * an older runtime — because a cap enforced on the way out is still a cap.
 */
async function readUpTo(res: Response, max: number): Promise<Uint8Array> {
  if (res.body === null) return new Uint8Array(await res.arrayBuffer()).slice(0, max);
  const reader = res.body.getReader();
  const chunks: Uint8Array[] = [];
  let total = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    if (!value) continue;
    chunks.push(value);
    total += value.byteLength;
    if (total >= max) {
      // Cancel rather than drain: there is no reason to receive 46 MB we have
      // already decided not to keep.
      await reader.cancel();
      break;
    }
  }
  const joined = new Uint8Array(total);
  let at = 0;
  for (const c of chunks) {
    joined.set(c, at);
    at += c.byteLength;
  }
  return total > max ? joined.slice(0, max) : joined;
}

export const nflverseAdapter: SourceAdapter = {
  manifest: nflverse,
  vocabulary: { concepts: NFLVERSE_CONCEPTS, mappings: NFLVERSE_MAPPINGS },
  linesDueBy,

  plan(ctx: PlanContext): FetchPlan {
    // Pure: no clock read, no network, no randomness. `ctx.now` is passed in so
    // the same inputs always produce the same plan, which is what makes fixture
    // replay a real test rather than a ceremony.
    if (ctx.cron === GAME_DAY_CRON) return gameDayPlan(ctx.now);
    const cleared = new Set(
      nflverse.resources.filter((r) => r.commercialUse).map((r) => r.resource),
    );
    const current = seasonOf(ctx.now);
    const season = String(current);
    const items: FetchItem[] = [];
    for (const p of PLANNED) {
      if (!cleared.has(p.resource)) continue;
      if (!p.partitioned) {
        items.push({ resource: p.resource, url: assetUrl(p.resource) });
        continue;
      }
      // One item per season in the window, newest first so a run that runs out
      // of time has fetched the seasons anybody is asking about.
      const span = p.seasons ?? 1;
      for (let back = 0; back < span; back++) {
        const partition = String(current - back);
        items.push({
          resource: p.resource,
          url: assetUrl(p.resource, partition),
          partition,
          ...(p.maxBytes === undefined ? {} : { maxBytes: p.maxBytes }),
        });
      }
    }
    return {
      items,
      because:
        items.length === 0
          ? 'no usable resource declared'
          : `${items.length} resource(s) cleared for use, season ${season}, as of ${ctx.now}`,
    };
  },

  async probe(item: FetchItem, ctx: FetchContext): Promise<ProbeResult> {
    const res = await ctx.fetch(timestampUrl(item.resource), { headers: { 'User-Agent': UA } });
    if (!res.ok) {
      // A probe that fails is not a failed run. We simply learned nothing, so
      // fall back to fetching and comparing hashes.
      return { changed: 'unknown' };
    }
    const token = (await res.text()).trim();
    return { changed: true, token };
  },

  async fetch(item: FetchItem, ctx: FetchContext): Promise<StoredCapture> {
    // A byte cap becomes a Range request. 206 is the success code for one; 200
    // means the server ignored the header and sent the whole asset, which is
    // not an error — the extractor works from the top of the file either way.
    const headers: Record<string, string> = { 'User-Agent': UA };
    if (item.maxBytes !== undefined) headers['Range'] = `bytes=0-${item.maxBytes - 1}`;
    const res = await ctx.fetch(item.url, { headers });
    if (!res.ok) {
      throw new Error(`nflverse: ${item.url} returned ${res.status}`);
    }
    // THE CAP IS OURS TO ENFORCE, not the server's to honor. A 200 to a Range
    // request means it ignored the header, and `arrayBuffer()` then reads the
    // whole asset: the depth chart is 46.8 MB, which becomes ~94 MB once
    // decoded to UTF-16, inside an isolate with a 128 MB ceiling — and all of
    // it goes to R2 as a capture of something we asked 400 KB of (audit 2.22).
    // Reading the stream ourselves costs one chunk past the cap.
    const body =
      item.maxBytes === undefined
        ? new Uint8Array(await res.arrayBuffer())
        : await readUpTo(res, item.maxBytes);
    return { id: '', resource: item.resource, fetchedAt: ctx.now, body };
  },

  extract(capture: StoredCapture, ctx: ExtractContext): ExtractResult {
    // Bytes are decoded here and nowhere earlier: `fetch` stores them verbatim
    // so a capture stays byte-reproducible and replayable.
    const text = new TextDecoder('utf-8').decode(capture.body);
    const observedAt = capture.fetchedAt;
    // The source values already mapped for this source, by domain. NOT our own
    // vocabulary: nflverse's "Did Not Participate In Practice" will never be a
    // term in a SportDefinition, so checking it against one is a category
    // error. See ICD-S07 §5.
    const vocabulary = ctx.vocabulary;

    switch (capture.resource) {
      case SCHEDULES:
        return extractSchedules(text, { observedAt });
      case PLAYERS:
        return extractPlayers(text, { observedAt, vocabulary });
      case ROSTERS:
        return extractRosters(text, { observedAt, vocabulary });
      case INJURIES:
        return extractInjuries(text, { observedAt, vocabulary });
      case STATS:
        return extractStatLines(text, { observedAt, vocabulary });
      case TEAM_STATS:
        return extractTeamStatLines(text, { observedAt });
      case DEPTH:
        return extractDepthCharts(text, { observedAt, vocabulary });
      default:
        throw new Error(`nflverse: no extractor for resource "${capture.resource}"`);
    }
  },
};
